<script setup lang="ts">
import { onMounted, ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import UIKit from '@pocketprep/ui-kit'
import ReadinessReportModal from '@/components/ReadinessReport/ReadinessReportModal.vue'
import { analyticsModule } from '@/store/analytics/module'
import { readinessModule } from '@/store/readiness/module'
import { screenModule } from '@/store/screen/module'

const route = useRoute()

const {
    InfoMessage,
    Button: PocketButton,
} = UIKit

defineProps<{
    isDarkMode?: boolean
}>()

const emit = defineEmits<{
    'isBannerVisible': [ isBannerVisible: boolean ]
}>()

const isShowingReadinessReportModal = ref(false)
const readinessReportVariantValue = ref<'control-no-report' | 'v2-generic-factors' | 'v3-passer-insights' | null>(null)

const breakpoint = computed(() => {
    return screenModule.getters.getBreakpoint()
})

const canSeeReadinessReportBanner = computed(() => {
    return readinessModule.getters.getCanSeeReadinessReport()
})

// canSeeReadinessReportBanner checks everything except Amplitude; isBannerVisible takes the experiment into account
const isBannerVisible = computed(() => {
    return canSeeReadinessReportBanner.value && (
        readinessReportVariantValue.value === 'v2-generic-factors'
        || readinessReportVariantValue.value === 'v3-passer-insights'
    )
})

const isPasserInsightReport = computed(() => {
    return readinessReportVariantValue.value === 'v3-passer-insights'
})

onMounted(async () => {
    if (canSeeReadinessReportBanner.value) {
        const readinessReportVariant = await analyticsModule.actions
            .amplitudeFetchVariant('readiness-report-v3-passer-insights')
        const variantValue = readinessReportVariant?.value as (
            'v2-generic-factors' | 'v3-passer-insights' | undefined
        ) || null
        readinessReportVariantValue.value = variantValue

        if (isBannerVisible.value) {
            const amplitudeReadinessProps = readinessModule.getters.getAmplitudeReadinessProps(route)
            analyticsModule.actions.amplitudeTrack('Readiness Banner Viewed', amplitudeReadinessProps)
        }
    }

    emit('isBannerVisible', isBannerVisible.value)
})

const generateReportClicked = () => {
    isShowingReadinessReportModal.value = true

    const amplitudeReadinessProps = readinessModule.getters.getAmplitudeReadinessProps(route)
    analyticsModule.actions.amplitudeTrack('Readiness Calculate Button Clicked', amplitudeReadinessProps)
}
</script>

<template>
    <div
        v-if="canSeeReadinessReportBanner
            && (readinessReportVariantValue === 'v2-generic-factors'
                || readinessReportVariantValue === 'v3-passer-insights'
            )
        "
        class="readiness-report-banner"
    >
        <ReadinessReportModal
            v-if="isShowingReadinessReportModal"
            :variant="readinessReportVariantValue"
            @close="isShowingReadinessReportModal = false"
        />
        <div class="readiness-report-banner__banner">
            <img
                v-if="breakpoint === 'black-bear'"
                class="readiness-report-banner__mobile-swirl"
                alt=""
                src="@/assets/readiness/mobile-banner-swirl.png"
            />
            <img
                v-else
                class="readiness-report-banner__swirl"
                alt=""
                src="@/assets/readiness/banner-swirl.png"
            />
            <InfoMessage
                class="readiness-report-banner__message"
                :is-dark-mode="isDarkMode"
            >
                <div class="readiness-report-banner__message-text">
                    {{ `Your personalized ${isPasserInsightReport
                        ? 'Passer Insights'
                        : 'Exam Readiness'} Report is here!`
                    }}
                </div>
                <PocketButton
                    type="tertiary"
                    :is-dark-mode="isDarkMode"
                    @click="generateReportClicked"
                >
                    {{ `Generate ${isPasserInsightReport ? 'Passer Insights' : 'Readiness'} Report` }}
                </PocketButton>
            </InfoMessage>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.readiness-report-banner {
    &__banner {
        display: flex;
        position: relative;
    }

    &__mobile-swirl {
        position: absolute;
        left: 1px;
        top: 1px;
        height: 12px;
        width: calc(100% - 2px);
        z-index: 2;
        border-top-left-radius: 5px;
        border-top-right-radius: 5px;
    }

    &__swirl {
        position: absolute;
        left: 0;
        top: 0;
        height: 100%;
        width: auto;
        z-index: 2;
    }

    &__message {
        font-weight: 600;
        position: relative;
        z-index: 1;
    }

    &__message-text {
        @include breakpoint(black-bear) {
            line-height: 20px;
            margin-bottom: 12px;
        }
    }

    :deep(.info-message) {
        width: 100%;
        padding: 12px 15px 12px 22px;
        background-color: $manilla;
        border: 1px solid $banana-bread;

        .info-message__content {
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;

            @include breakpoint(black-bear) {
                flex-direction: column;
                align-items: flex-start;
            }
        }
    }

    :deep(.info-message--dark) {
        background-color: rgb(255, 164, 0, 0.20);
        border: 1px solid rgb(255, 164, 0, 0.50);
    }

    @include breakpoint(black-bear) {
        :deep(.info-message) {
            padding: 24px 16px 12px 16px;
        }
    }
}
</style>
